import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from './LoadingSpinner';
// Email verification banner removed globally per requirement

const ProtectedRoute = ({
  children,
  requiredRole = null,
  requiredRoles = [],
  requiredPermission = null,
  requireEmailVerification = false,
  redirectTo = '/login',
  fallback = null,
}) => {
  const {
    isAuthenticated,
    user,
    isInitialized,
    hasRole,
    hasAnyRole,
    hasPermission,
    isEmailVerified
  } = useAuth();
  const location = useLocation();

  // Show loading while auth is initializing
  if (!isInitialized) {
    return fallback || <LoadingSpinner />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return (
      <Navigate 
        to={redirectTo} 
        state={{ from: location.pathname }} 
        replace 
      />
    );
  }

  // Check single required role
  if (requiredRole && !hasRole(requiredRole)) {
    console.log('[DEBUG-ROUTE] Redirecting to /unauthorized. Required:', requiredRole, 'Has:', user?.role, user?.roles);
    return (
      <Navigate 
        to="/unauthorized" 
        state={{ 
          from: location.pathname,
          requiredRole,
          userRole: user?.role 
        }} 
        replace 
      />
    );
  }

  // Check multiple required roles (user must have at least one)
  if (requiredRoles.length > 0 && !hasAnyRole(requiredRoles)) {
    console.log('[DEBUG-ROUTE] Redirecting to /unauthorized. Required:', requiredRoles, 'Has:', user?.role, user?.roles);
    return (
      <Navigate 
        to="/unauthorized" 
        state={{ 
          from: location.pathname,
          requiredRoles,
          userRole: user?.role 
        }} 
        replace 
      />
    );
  }

  // Check a granular, admin-controlled module permission (e.g. QR Chat),
  // in addition to the broad role check above. Superadmins always pass.
  if (requiredPermission) {
    const isSuperAdminUser = Boolean(user?.is_superadmin)
      || String(user?.role_type || '').toLowerCase() === 'superadmin'
      || String(user?.role || '').toLowerCase() === 'superadmin'
      || String(user?.role_name || '').toLowerCase() === 'superadmin';

    // A vendor's own account needs their vendor's team_chat_enabled/
    // vendor_whatsapp_enabled switch (set by a platform admin) on top of the
    // role permission below — scoped to those two keys only, so it never
    // affects other modules.
    const vendorGateOk =
      (requiredPermission.key !== 'team_chat' || !user?.vendor_id || Boolean(user?.vendor_team_chat_enabled)) &&
      (requiredPermission.key !== 'vendor_whatsapp' || !user?.vendor_id || Boolean(user?.vendor_whatsapp_enabled));

    if (!isSuperAdminUser && (!vendorGateOk || !hasPermission(requiredPermission.key, requiredPermission.action || 'view'))) {
      return (
        <Navigate
          to="/unauthorized"
          state={{
            from: location.pathname,
            requiredPermission,
          }}
          replace
        />
      );
    }
  }

  // Check email verification requirement
  if (requireEmailVerification && !isEmailVerified()) {
    // Redirect quietly without showing any banner or message
    return <Navigate to="/profile" replace />;
  }

  // All checks passed, render the protected content
  return (
    <>
      {children}
    </>
  );
};

// Higher-order component for protecting routes
export const withProtection = (Component, options = {}) => {
  return (props) => (
    <ProtectedRoute {...options}>
      <Component {...props} />
    </ProtectedRoute>
  );
};

// Specific role-based route components
export const AdminRoute = ({ children, ...props }) => (
  <ProtectedRoute requiredRole="admin" {...props}>
    {children}
  </ProtectedRoute>
);

export const InstructorRoute = ({ children, ...props }) => (
  <ProtectedRoute requiredRoles={['admin', 'instructor']} {...props}>
    {children}
  </ProtectedRoute>
);

export const StudentRoute = ({ children, ...props }) => (
  <ProtectedRoute requiredRoles={['admin', 'instructor', 'student']} {...props}>
    {children}
  </ProtectedRoute>
);

// Route that requires email verification
export const VerifiedRoute = ({ children, ...props }) => (
  <ProtectedRoute requireEmailVerification={true} {...props}>
    {children}
  </ProtectedRoute>
);

export default ProtectedRoute;
